import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { applyV1MasterSettings, fmtRsPerShare } from './trading-rules';
import './NormalTrading.css';
import './NormalTradingV1.css';
import './NormalTradingV1Master.css';
import IstClock from './IstClock';

const BAND_OPTIONS = [
  { key: 'penny', label: 'Penny' },
  { key: 'medium', label: 'Medium' },
  { key: 'high', label: 'High' }
];

const SCAN_ENGINE_OPTIONS = [
  { key: 'nifty-100', label: 'Nifty 50 + Next 50 (recommended)' },
  { key: 'nifty-50', label: 'Nifty 50 only' },
  { key: 'profit-universe', label: 'Profit movers (PSU / volatile)' },
  { key: 'cnc-degen', label: 'Degen CNC (110 NSE+BSE)' }
];

const DEFAULT_FORM = {
  buyProfitRs: 5,
  holdProfitRs: 0.5,
  holdPartialMinRs: 0.3,
  holdPartialMaxRs: 0.5,
  holdEscapeMinRs: 0.10,
  holdEscapeMaxRs: 1000,
  maxHoldMinutes: 10,
  holdSoftAfterMin: 0,
  holdStopAfterMin: 0,
  minBuyQty: 15,
  maxBuyQty: 30,
  slotsMax: 5,
  capitalAmount: 50000,
  dailyTargetRs: 1000,
  dailyNetTargetRs: 1000,
  minNetProfitPerTradeRs: 80,
  netProfitMode: true,
  unlimitedCapital: false,
  fillIntervalSec: 60,
  scanEngine: 'nifty-100',
  candleConfirmBuy: true,
  candleConfirmTopN: 28,
  feeAwareMomentum: true,
  priceBand: 'medium',
  sellTabAutoSoldSec: 10,
  holdStopLossPct: 1,
  priceBands: {
    penny: { min: 1, max: 10, label: 'Penny' },
    medium: { min: 100, max: 1000, label: 'Medium' },
    high: { min: 40, max: 200, label: 'High' }
  }
};

function normalizeForm(data) {
  const base = { ...DEFAULT_FORM, ...(data || {}) };
  base.priceBands = {
    ...DEFAULT_FORM.priceBands,
    ...(data?.priceBands || {})
  };
  for (const key of ['penny', 'medium', 'high']) {
    base.priceBands[key] = {
      ...DEFAULT_FORM.priceBands[key],
      ...(base.priceBands[key] || {})
    };
  }
  return base;
}

export default function NormalTradingV1Master({
  apiBase = '/api/normal-v1',
  boardPath = '/NormalTrading',
  pageTitle = 'V1 Master Settings'
}) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${apiBase}/master-settings`, { timeout: 12000 });
      const next = normalizeForm(res.data?.settings);
      setForm(next);
      applyV1MasterSettings(res.data?.settings);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load settings');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setBandField = (bandKey, field, value) => {
    setForm((prev) => ({
      ...prev,
      priceBands: {
        ...prev.priceBands,
        [bandKey]: {
          ...prev.priceBands[bandKey],
          [field]: value
        }
      }
    }));
  };

  const activeBand = form.priceBands[form.priceBand] || form.priceBands.penny;

  const summary = `Capital ₹${Number(form.capitalAmount || 0).toLocaleString('en-IN')} · qty ${form.minBuyQty}–${form.maxBuyQty} sh · net ≥ ₹${form.minNetProfitPerTradeRs}/trade · day net goal ₹${form.dailyNetTargetRs || form.dailyTargetRs} · BUY +${fmtRsPerShare(form.buyProfitRs)}/sh · ${activeBand.label} ₹${activeBand.min}–${activeBand.max}`;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await axios.post(`${apiBase}/master-settings`, form, { timeout: 15000 });
      const saved = normalizeForm(res.data?.settings);
      setForm(saved);
      applyV1MasterSettings(res.data?.settings);
      const okMsg = 'Data is saved in Normal Trading V1 Master';
      setMessage(okMsg);
      window.alert(okMsg);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nt-desktop nt-v1 nt-v1-master nt-no-sidebar">
      <main className="nt-main">
        <header className="nt-header nt-header-compact">
          <div>
            <h1>{pageTitle}</h1>
            <p className="nt-subtitle">
              Set profit targets, hold timer, and stock price band — only stocks in the selected band appear in the BUY tab.
            </p>
          </div>
          <div className="nt-v1-master-clock">
            <IstClock />
          </div>
          <Link to={boardPath} className="nt-v1-btn primary">
            ← Back to V1 board
          </Link>
        </header>

        {message && <div className="nt-v1-toast">{message}</div>}
        {error && <div className="nt-v1-master-error">{error}</div>}

        {loading ? (
          <p className="nt-v1-master-loading">Loading master settings…</p>
        ) : (
          <form className="nt-v1-master-form card" onSubmit={save}>
            <section className="nt-v1-master-section">
              <h2>Capital (full-day buying)</h2>
              <p className="nt-v1-master-hint">
                Bot deploys only this capital all day. Sell frees cash for new buys. New buys stop when deployed capital hits the limit.
              </p>
              <div className="nt-v1-master-grid">
                <label>
                  <span>Capital amount (₹)</span>
                  <input
                    type="number"
                    step="1000"
                    min="1000"
                    max="5000000"
                    value={form.capitalAmount}
                    onChange={(e) => setField('capitalAmount', Number(e.target.value))}
                  />
                  <small>Default ₹50,000 — max cash deployed across open slots</small>
                </label>
                <label>
                  <span>Day earnings counter (₹)</span>
                  <input
                    type="number"
                    step="100"
                    min="100"
                    max="1000000"
                    value={form.dailyTargetRs}
                    onChange={(e) => setField('dailyTargetRs', Number(e.target.value))}
                  />
                  <small>Display milestone — never hard-stops trading</small>
                </label>
                <label>
                  <span>Day net profit goal (₹)</span>
                  <input
                    type="number"
                    step="100"
                    min="100"
                    max="1000000"
                    value={form.dailyNetTargetRs ?? form.dailyTargetRs}
                    onChange={(e) => setField('dailyNetTargetRs', Number(e.target.value))}
                  />
                  <small>Target after brokerage/STT/DP — path: ~12 exits × ₹80 net</small>
                </label>
                <label className="nt-v1-master-check">
                  <span>Unlimited capital</span>
                  <input
                    type="checkbox"
                    checked={!!form.unlimitedCapital}
                    onChange={(e) => setField('unlimitedCapital', e.target.checked)}
                  />
                  <small>Off = use capital amount above (recommended)</small>
                </label>
              </div>
            </section>

            <section className="nt-v1-master-section">
              <h2>Net profit after Zerodha charges</h2>
              <p className="nt-v1-master-hint">
                Auto-sell only when estimated net (gross − brokerage − STT − GST…) ≥ min below.
                Report charges follow Master order type (Intraday MIS vs Delivery CNC).
              </p>
              <div className="nt-v1-master-grid">
                <label>
                  <span>Order / charge type</span>
                  <select
                    value={form.orderType === 'CNC' ? 'CNC' : 'MIS'}
                    onChange={(e) => setField('orderType', e.target.value)}
                  >
                    <option value="MIS">Intraday (MIS) — Zerodha brokerage ~0.03%</option>
                    <option value="CNC">Delivery (CNC) — ₹0 brokerage + higher STT/DP</option>
                  </select>
                  <small>Matches Zerodha calculator. Default: Intraday.</small>
                </label>
                <label>
                  <span>Min net ₹ / trade (after charges)</span>
                  <input
                    type="number"
                    step="10"
                    min="0"
                    max="5000"
                    value={form.minNetProfitPerTradeRs}
                    onChange={(e) => setField('minNetProfitPerTradeRs', Number(e.target.value))}
                  />
                  <small>0 = legacy gross-only sells · 80 = block fee-losing exits</small>
                </label>
              </div>
            </section>

            <section className="nt-v1-master-section">
              <h2>Profit targets</h2>
              <div className="nt-v1-master-grid">
                <label>
                  <span>BUY tab target (₹/share)</span>
                  <input
                    type="number"
                    step="0.05"
                    min="0.1"
                    value={form.buyProfitRs}
                    onChange={(e) => setField('buyProfitRs', Number(e.target.value))}
                  />
                  <small>
                    Target ₹/share for BUY → SELL. Your value is kept on save (not auto-raised).
                    {Number(form.minNetProfitPerTradeRs) > 0
                      ? ` With min net ₹${form.minNetProfitPerTradeRs} and max qty ${form.maxBuyQty}, fee floor is often ~₹${(
                          Math.max(
                            Number(form.minNetProfitPerTradeRs || 0) / Math.max(1, Number(form.maxBuyQty) || 1),
                            3.5
                          )
                        ).toFixed(2)}/sh — below that BUY may stay empty.`
                      : ''}
                  </small>
                  {form.buyProfitBelowNetFloor && form.minBuyProfitRsForNet != null ? (
                    <small className="nt-v1-warn">
                      Warning: +₹{fmtRsPerShare(form.buyProfitRs)}/sh is below fee floor ~₹
                      {Number(form.minBuyProfitRsForNet).toFixed(2)}/sh. Raise BUY target, or lower min net / raise max qty.
                    </small>
                  ) : null}
                </label>
                <label>
                  <span>HOLD tab target (₹/share)</span>
                  <input
                    type="number"
                    step="0.05"
                    min="0.1"
                    value={form.holdProfitRs}
                    onChange={(e) => setField('holdProfitRs', Number(e.target.value))}
                  />
                  <small>
                    Full +₹/sh on HOLD → SELL anytime. Independent of BUY target — changing BUY does not change this.
                  </small>
                </label>
                <label>
                  <span>BUY window (minutes)</span>
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={form.maxHoldMinutes}
                    onChange={(e) => setField('maxHoldMinutes', Number(e.target.value))}
                  />
                  <small>After this on BUY: if LTP is −1% or more from buy → SELL → Sold Today; else if no profit target → HOLD</small>
                </label>
                <label>
                  <span>SELL tab → Sold Today (seconds)</span>
                  <input
                    type="number"
                    min="0"
                    max="600"
                    step="1"
                    value={form.sellTabAutoSoldSec ?? 10}
                    onChange={(e) => setField('sellTabAutoSoldSec', Number(e.target.value))}
                  />
                  <small>Every share on SELL tab auto-moves to Sold Today after this many seconds</small>
                </label>
                <label>
                  <span>BUY/HOLD −% from buy → Sold Today</span>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    step="0.1"
                    value={form.holdStopLossPct ?? 1}
                    onChange={(e) => setField('holdStopLossPct', Number(e.target.value))}
                  />
                  <small>
                    After {form.maxHoldMinutes || 10} min on BUY (or anytime on HOLD): if LTP is this % below buy price → SELL → Sold Today. 1 = −1%. 0 = off.
                  </small>
                </label>
              </div>
            </section>

            <section className="nt-v1-master-section">
              <h2>Scan universe (Einstein pipeline)</h2>
              <p className="nt-v1-master-hint">
                Controls which stocks the CNC pipeline scans. Use Nifty 50 + Next 50 for liquid index names only — not PSU midcaps like SAIL unless they are in the index.
              </p>
              <div className="nt-v1-master-band-row">
                <label>
                  <span>Scan engine</span>
                  <select
                    value={form.scanEngine || 'nifty-100'}
                    onChange={(e) => setField('scanEngine', e.target.value)}
                  >
                    {SCAN_ENGINE_OPTIONS.map((b) => (
                      <option key={b.key} value={b.key}>{b.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="nt-v1-master-band-row" style={{ marginTop: 12 }}>
                <label className="nt-v1-master-check">
                  <input
                    type="checkbox"
                    checked={form.candleConfirmBuy !== false}
                    onChange={(e) => setField('candleConfirmBuy', e.target.checked)}
                  />
                  <span>
                    Candle UP confirm
                    <small style={{ display: 'block', fontWeight: 500, opacity: 0.85, marginTop: 4 }}>
                      Einstein quote rank → 5m Uptrend only → then BUY fill.
                    </small>
                  </span>
                </label>
              </div>
              <div className="nt-v1-master-band-row" style={{ marginTop: 8 }}>
                <label className="nt-v1-master-check">
                  <input
                    type="checkbox"
                    checked={form.feeAwareMomentum !== false}
                    onChange={(e) => setField('feeAwareMomentum', e.target.checked)}
                    disabled={form.candleConfirmBuy === false}
                  />
                  <span>
                    Fee-aware momentum (recommended)
                    <small style={{ display: 'block', fontWeight: 500, opacity: 0.85, marginTop: 4 }}>
                      If Nifty weak: only green RS leaders that beat the index · volume OK · hard net ≥ Master min.
                      Or use V1 screen <strong>Rollback to candle confirm</strong>.
                    </small>
                  </span>
                </label>
              </div>
            </section>

            <section className="nt-v1-master-section">
              <h2>Stock price band (BUY tab filter)</h2>
              <p className="nt-v1-master-hint">
                Choose Penny, Medium, or High — then set min/max share price. Only stocks in this range enter the AI pipeline BUY tab.
              </p>
              <div className="nt-v1-master-band-row">
                <label>
                  <span>Active band</span>
                  <select
                    value={form.priceBand}
                    onChange={(e) => setField('priceBand', e.target.value)}
                  >
                    {BAND_OPTIONS.map((b) => (
                      <option key={b.key} value={b.key}>{b.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Min price (₹)</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={activeBand.min}
                    onChange={(e) => setBandField(form.priceBand, 'min', Number(e.target.value))}
                  />
                </label>
                <label>
                  <span>Max price (₹)</span>
                  <input
                    type="number"
                    step="0.5"
                    min="1"
                    value={activeBand.max}
                    onChange={(e) => setBandField(form.priceBand, 'max', Number(e.target.value))}
                  />
                </label>
              </div>
              <p className="nt-v1-master-band-active">
                Active filter: <strong>{activeBand.label}</strong> stocks at ₹{activeBand.min}–₹{activeBand.max}/share
              </p>
            </section>

            <section className="nt-v1-master-section">
              <h2>Pipeline &amp; quantity</h2>
              <p className="nt-v1-master-band-active">
                Bot sizes qty from free cash ÷ free slots ÷ price, then clamps to this range (never buys without fund capital).
              </p>
              <div className="nt-v1-master-grid">
                <label>
                  <span>Min qty per stock</span>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={form.minBuyQty}
                    onChange={(e) => setField('minBuyQty', Number(e.target.value))}
                  />
                  <small>15+ recommended so DP ₹16.50 is diluted</small>
                </label>
                <label>
                  <span>Max qty per stock</span>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={form.maxBuyQty}
                    onChange={(e) => setField('maxBuyQty', Number(e.target.value))}
                  />
                  <small>30 recommended for ~₹80–100 net/exit path</small>
                </label>
                <label>
                  <span>Max BUY slots</span>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={form.slotsMax}
                    onChange={(e) => setField('slotsMax', Number(e.target.value))}
                  />
                </label>
                <label>
                  <span>Pipeline fill interval (seconds)</span>
                  <input
                    type="number"
                    min="30"
                    max="300"
                    value={form.fillIntervalSec}
                    onChange={(e) => setField('fillIntervalSec', Number(e.target.value))}
                  />
                  <small>How often 1 new stock enters BUY (default 60s = 1/min)</small>
                </label>
              </div>
            </section>

            <footer className="nt-v1-master-foot">
              <p className="nt-v1-master-summary">{summary}</p>
              <div className="nt-v1-master-actions">
                <button type="button" className="nt-v1-btn" onClick={loadSettings} disabled={saving}>
                  Reset form
                </button>
                <button type="submit" className="nt-v1-btn primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save V1 Master settings'}
                </button>
              </div>
            </footer>
          </form>
        )}
      </main>
    </div>
  );
}
